/**
 * Lead Intake Function
 * 
 * The SINGLE entry point for accepting new leads from external sources.
 * This function:
 * 1. Validates the incoming payload
 * 2. Normalizes the data
 * 3. Persists the lead
 * 4. Triggers routing through the Orchestrator
 * 5. Updates lifecycle state based on outcome
 * 
 * NO routing logic lives here - this is pure intake plumbing.
 */

import type {
  IncomingLeadPayload,
  NormalizedLead,
  IntakeResult,
  IntakeLifecycleStatus,
} from './leadTypes';
import { validateLeadPayload, validateLeadPayloadClassified } from './validation';
import {
  routeIncomingLead,
  type RoutingResult,
  type RoutingContext,
} from '../routingOrchestrator';
import { 
  type LifecycleLead, 
  createReviewRequiredLead 
} from '../leadStateMachine';
import { persistReviewRequiredLead } from './persistLead';
import { normalizeLeadProfile } from './normalizeLeadProfile';

// --- Types ---

export interface IntakeOptions {
  /**
   * Routing context (agents, weights, etc.)
   * Required for live routing.
   */
  routingContext?: RoutingContext;
  
  /**
   * Custom ID generator
   */
  generateId?: () => string;
  
  /**
   * Default source if not provided in payload
   */
  defaultSource?: string;
  
  /**
   * Callback when lead is persisted
   */
  onLeadPersisted?: (lead: LifecycleLead) => void;
  
  /**
   * Callback when routing completes
   */
  onRoutingComplete?: (result: RoutingResult) => void;
  
  /**
   * Callback when lead requires review (soft validation failure)
   */
  onReviewRequired?: (lead: LifecycleLead) => void;
  
  /**
   * Enable soft validation (flag ambiguous leads for review instead of rejecting)
   */
  enableSoftValidation?: boolean;
  
  /**
   * Enable debug logging
   */
  debug?: boolean;
}

// --- Main Intake Function ---

/**
 * Process an incoming lead through the intake pipeline.
 * 
 * This is the only valid entry point for new leads entering the system.
 */
export function intakeLead(
  payload: IncomingLeadPayload,
  options: IntakeOptions = {}
): IntakeResult {
  const startedAt = Date.now();
  const {
    routingContext,
    generateId,
    defaultSource = 'api',
    onLeadPersisted,
    onRoutingComplete,
    onReviewRequired,
    enableSoftValidation = true,
    debug = false,
  } = options;
  
  if (debug) {
    console.log('[Intake] Processing lead:', payload.phone?.slice(-4), payload.leadType);
  }
  
  // --- Step 1: Validate with classification ---
  if (enableSoftValidation) {
    const classified = validateLeadPayloadClassified(payload, {
      generateId,
      defaultSource,
    });
    
    // HARD failure - discard
    if (classified.outcome === 'HARD_FAILURE') {
      if (debug) {
        console.log('[Intake] HARD validation failed:', classified.errors);
      }
      
      return {
        success: false,
        intakeStatus: 'VALIDATION_FAILED',
        errors: classified.errors,
        processingDurationMs: Date.now() - startedAt,
      };
    }
    
    // SOFT failure - persist for review
    if (classified.outcome === 'SOFT_FAILURE' && classified.partialLead) {
      if (debug) {
        console.log('[Intake] SOFT validation failed - flagging for review:', classified.errors);
      }
      
      // Create a review-required lead
      const reviewLeadBase = createReviewRequiredLead(
        {
          id: classified.partialLead.id || `lead_${Date.now()}_review`,
          leadTypeId: classified.partialLead.leadTypeId || 'unknown',
          leadTypeName: classified.partialLead.leadTypeName || payload.leadType || 'Unknown',
          vertical: classified.partialLead.vertical || 'life_insurance',
          state: classified.partialLead.state || 'XX',
          estimatedValue: classified.partialLead.estimatedValue || 50,
        },
        classified.errors.map(e => ({
          field: e.field,
          message: e.message,
          code: e.code,
          severity: e.severity,
        }))
      );
      
      const reviewLead = {
        ...reviewLeadBase,
        leadPayload: payload,
        leadProfile: normalizeLeadProfile(payload, reviewLeadBase.leadTypeId),
      };
      
      // Persist to database (async, fire and forget)
      persistReviewRequiredLead(reviewLead).catch(err => {
        console.error('[Intake] Failed to persist review lead:', err);
      });
      
      // Notify callback
      if (onReviewRequired) {
        onReviewRequired(reviewLead);
      }
      
      return {
        success: true, // Lead was accepted, just flagged for review
        leadId: reviewLead.id,
        intakeStatus: 'RECEIVED', // Not routed, but received
        routingOutcome: undefined, // No routing attempted
        processingDurationMs: Date.now() - startedAt,
      };
    }
    
    // VALID - proceed with routing
    if (classified.outcome === 'VALID' && classified.normalizedLead) {
      return processValidLead(classified.normalizedLead, {
        routingContext,
        onLeadPersisted,
        onRoutingComplete,
        debug,
        startedAt,
      });
    }
  }
  
  // --- Fallback: Standard validation (no soft handling) ---
  const validation = validateLeadPayload(payload, {
    generateId,
    defaultSource,
  });
  
  if (!validation.valid || !validation.normalizedLead) {
    if (debug) {
      console.log('[Intake] Validation failed:', validation.errors);
    }
    
    return {
      success: false,
      intakeStatus: 'VALIDATION_FAILED',
      errors: validation.errors,
      processingDurationMs: Date.now() - startedAt,
    };
  }
  
  return processValidLead(validation.normalizedLead, {
    routingContext,
    onLeadPersisted,
    onRoutingComplete,
    debug,
    startedAt,
  });
}

/**
 * Process a validated lead through routing.
 */
function processValidLead(
  normalizedLead: NormalizedLead,
  options: {
    routingContext?: RoutingContext;
    onLeadPersisted?: (lead: LifecycleLead) => void;
    onRoutingComplete?: (result: RoutingResult) => void;
    debug?: boolean;
    startedAt: number;
  }
): IntakeResult {
  const { routingContext, onLeadPersisted, onRoutingComplete, debug, startedAt } = options;
  
  if (debug) {
    console.log('[Intake] Lead validated:', normalizedLead.id);
  }
  
  // --- Step 2: Check if routing context available ---
  if (!routingContext) {
    // No routing context = just validate and return
    // This is useful for validation-only checks
    return {
      success: true,
      leadId: normalizedLead.id,
      intakeStatus: 'VALIDATED',
      processingDurationMs: Date.now() - startedAt,
    };
  }
  
  // --- Step 3: Route through Orchestrator ---
  const routingResult = routeIncomingLead(
    {
      lead: {
        id: normalizedLead.id,
        leadTypeId: normalizedLead.leadTypeId,
        leadTypeName: normalizedLead.leadTypeName,
        vertical: normalizedLead.vertical,
        state: normalizedLead.state,
        estimatedValue: normalizedLead.estimatedValue,
      },
      sourcePayload: normalizedLead.sourcePayload,
      source: 'real',
      debug,
    },
    routingContext
  );
  
  if (debug) {
    console.log('[Intake] Routing outcome:', routingResult.outcome);
  }
  
  // Notify callbacks
  if (onLeadPersisted) {
    onLeadPersisted(routingResult.lead);
  }
  
  if (onRoutingComplete) {
    onRoutingComplete(routingResult);
  }
  
  // --- Step 4: Map outcome to intake status ---
  const intakeStatus = mapOutcomeToIntakeStatus(routingResult.outcome);
  
  return {
    success: routingResult.success,
    leadId: normalizedLead.id,
    intakeStatus,
    routingOutcome: routingResult.outcome,
    assignedAgentId: routingResult.deliveryImpact.agentId,
    assignedAgentName: routingResult.deliveryImpact.agentName,
    chargedAmount: routingResult.deliveryImpact.chargedAmount,
    contractId: routingResult.deliveryImpact.contractId,
    retryCount: routingResult.lead.retryCount,
    nextRetryAt: routingResult.lead.nextRetryAt,
    processingDurationMs: Date.now() - startedAt,
  };
}

/**
 * Map routing outcome to intake lifecycle status.
 */
function mapOutcomeToIntakeStatus(outcome: RoutingResult['outcome']): IntakeLifecycleStatus {
  switch (outcome) {
    case 'ROUTED':
      return 'ROUTED';
    case 'RETRY_QUEUED':
      return 'RETRY_QUEUED';
    case 'STOCKED':
      return 'STOCKED';
    case 'UNSOLD':
      return 'UNSOLD';
    case 'VALIDATION_FAILED':
      return 'VALIDATION_FAILED';
    default:
      return 'UNSOLD';
  }
}

// --- Batch Intake ---

/**
 * Process multiple leads through intake.
 * Returns results for each lead.
 */
export function intakeLeadBatch(
  payloads: IncomingLeadPayload[],
  options: IntakeOptions = {}
): IntakeResult[] {
  return payloads.map(payload => intakeLead(payload, options));
}

// --- Validation-Only ---

/**
 * Validate a lead payload without routing.
 * Useful for pre-flight checks.
 */
export function validateLeadOnly(
  payload: IncomingLeadPayload,
  options: Pick<IntakeOptions, 'generateId' | 'defaultSource'> = {}
): IntakeResult {
  const startedAt = Date.now();
  
  const validation = validateLeadPayload(payload, {
    generateId: options.generateId,
    defaultSource: options.defaultSource ?? 'validation',
  });
  
  if (!validation.valid) {
    return {
      success: false,
      intakeStatus: 'VALIDATION_FAILED',
      errors: validation.errors,
      processingDurationMs: Date.now() - startedAt,
    };
  }
  
  return {
    success: true,
    leadId: validation.normalizedLead!.id,
    intakeStatus: 'VALIDATED',
    processingDurationMs: Date.now() - startedAt,
  };
}

// --- Dev Utilities ---

/**
 * Create a test lead payload with minimal required fields.
 */
export function createTestLeadPayload(
  overrides: Partial<IncomingLeadPayload> = {}
): IncomingLeadPayload {
  return {
    name: 'John Doe',
    phone: '555-123-4567',
    state: 'TX',
    leadType: 'Final Expense',
    source: 'internal_test',
    ...overrides,
  };
}
